import { formatDistanceToNow } from 'date-fns';
import {
  CheckCircleIcon,
  ClockCounterClockwiseIcon,
  ProhibitIcon,
  SpinnerIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import { Num, Plural, T, useGT } from 'gt-react';
import {
  type AgentRunCategory,
  type AgentRunSummary,
  type AgentRunTriggerKind,
} from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import { Switch } from 'src/components/ui/switch';
import { cn } from 'src/lib/utils';
import { formatRunDuration } from '../agentPageRuns';
import { DateRangePicker } from 'src/components/common/date-pickers/date-range-picker';
import {
  fromDayPickerRange,
  toDayPickerRange,
} from 'src/components/common/date-pickers/dateRangePresets';
import {
  excludingCategories,
  hidesOnlySkipped,
  hidesSkipped,
  withRange,
  withSkippedHidden,
  type RunHistoryFilter,
} from '../agentRunHistoryFilter';
import { runIsSkipped, runNeverStarted } from '../runCategories';
import {
  historyEmptyReason,
  type HistoryEmptyReason,
} from '../historyEmptyState';
import { RunCategoryFilter } from './RunCategoryFilter';

function StatusIcon({
  status,
  notStarted,
}: {
  status: AgentRunSummary['status'];
  notStarted: boolean;
}) {
  if (status === 'running') {
    return (
      <SpinnerIcon className="size-4 shrink-0 animate-spin text-sky-600" />
    );
  }
  // A refused start is neither a green check nor the agent's own failure;
  // it still turns red when the gate failed it (resource held by a dead run).
  if (notStarted) {
    return (
      <ProhibitIcon
        weight="bold"
        className={cn(
          'size-4 shrink-0',
          status === 'failed' ? 'text-red-600' : 'text-stone-400',
        )}
      />
    );
  }
  if (status === 'completed') {
    return (
      <CheckCircleIcon
        weight="fill"
        className="size-4 shrink-0 text-emerald-600"
      />
    );
  }
  return <XCircleIcon weight="fill" className="size-4 shrink-0 text-red-600" />;
}

function useTriggerLabel() {
  const gt = useGT();
  return (kind: AgentRunTriggerKind | null | undefined): string | null => {
    switch (kind) {
      case 'manual':
        return gt('Started by hand', {
          context: 'how an agent run was started',
        });
      case 'schedule':
        return gt('Scheduled', { context: 'how an agent run was started' });
      case 'event':
        return gt('Triggered by an event', {
          context: 'how an agent run was started',
        });
      case 'api':
        return gt('Via API', { context: 'how an agent run was started' });
      case 'task_queue':
        return gt('Picked up from the queue', {
          context: 'how an agent run was started',
        });
      case 'workflow':
        return gt('From a workflow', {
          context: 'how an agent run was started',
        });
      default:
        return null;
    }
  };
}

function RunRow({
  run,
  selected,
  onSelect,
}: {
  run: AgentRunSummary;
  selected: boolean;
  onSelect: () => void;
}) {
  const triggerLabel = useTriggerLabel()(run.triggerKind);
  const duration = formatRunDuration(run.duration);
  const startedAgo = formatDistanceToNow(new Date(run.startedAt), {
    addSuffix: true,
  });
  const notStarted = runNeverStarted(run);
  const skipped = runIsSkipped(run);
  const detail = run.status === 'failed' ? run.error : null;

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected ? 'true' : undefined}
        className={cn(
          'flex w-full items-start gap-3 px-3 py-2 text-left transition-colors hover:bg-stone-50',
          selected && 'bg-stone-100 hover:bg-stone-100',
        )}
      >
        <span className="pt-0.5">
          <StatusIcon status={run.status} notStarted={notStarted} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <span className="truncate text-[13px] font-medium text-stone-900">
              {startedAgo}
            </span>
            <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
              {run.status === 'running' ? (
                <T context="run history row, run still in progress">running</T>
              ) : (
                (duration ?? '—')
              )}
            </span>
          </span>
          <span className="block truncate text-[11.5px] text-muted-foreground">
            {detail ? (
              <span className="text-red-700">{detail}</span>
            ) : skipped ? (
              <T context="run history row: the run did not start because the agent was only waiting (start conditions not met or its resource busy)">
                Skipped
              </T>
            ) : notStarted ? (
              <T context="run history row, the start gate refused the run before it did any work">
                Did not start
              </T>
            ) : (
              triggerLabel
            )}
          </span>
        </span>
      </button>
    </li>
  );
}

/**
 * The switch that hides skipped checks. Its label is the switch's accessible
 * name; the title explains what counts as skipped.
 */
function HideSkippedSwitch({
  filter,
  onFilterChange,
}: {
  filter: RunHistoryFilter;
  onFilterChange: (next: RunHistoryFilter) => void;
}) {
  const gt = useGT();
  return (
    <label
      className="flex cursor-pointer items-center gap-1.5 text-[11px] text-muted-foreground"
      title={gt(
        'Hide runs that did not start because the agent was only waiting: its start conditions were not met or a resource it needs was busy. Runs that could not start because of a problem, and runs started by hand, stay visible.',
        {
          context:
            'tooltip on the agent page history switch that hides skipped runs',
        },
      )}
    >
      <Switch
        checked={hidesSkipped(filter)}
        onCheckedChange={(hide) =>
          onFilterChange(withSkippedHidden(filter, hide))
        }
      />
      <T context="agent page history switch: hide runs that did not start because the agent was only waiting">
        Hide skipped
      </T>
    </label>
  );
}

/**
 * The History footer: how many runs the filter left out with a way to show
 * them (left), and "Show Older Runs" (right), on one row. When the filter
 * leaves out only skipped checks the link brings those back; otherwise it
 * clears the whole filter.
 */
function HistoryFooter({
  hiddenCount,
  onlySkippedHidden,
  onShowHidden,
  canLoadMore,
  onLoadMore,
  isLoadingMore,
}: {
  hiddenCount: number;
  onlySkippedHidden: boolean;
  onShowHidden: () => void;
  canLoadMore: boolean;
  onLoadMore: () => void;
  isLoadingMore: boolean;
}) {
  if (hiddenCount === 0 && !canLoadMore) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-border px-3 py-1.5 text-[11.5px] text-muted-foreground">
      {hiddenCount > 0 ? (
        <span className="flex flex-wrap items-center gap-x-1.5">
          {onlySkippedHidden ? (
            <T context="agent page history footer: how many runs that did not start (agent was only waiting) are hidden">
              <Num>{hiddenCount}</Num> skipped{' '}
              <Plural n={hiddenCount} one="run" other="runs" /> hidden
            </T>
          ) : (
            <T context="agent page history footer: how many runs the run-type filter hides">
              <Num>{hiddenCount}</Num>{' '}
              <Plural n={hiddenCount} one="run" other="runs" /> hidden
            </T>
          )}
          <span aria-hidden>·</span>
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-[11.5px]"
            onClick={onShowHidden}
          >
            {onlySkippedHidden ? (
              <T context="agent page history footer button: show the hidden skipped runs">
                Show Skipped
              </T>
            ) : (
              <T context="agent page history footer button: clear the run-type filter so every run is shown">
                Show All
              </T>
            )}
          </Button>
        </span>
      ) : (
        <span />
      )}
      {canLoadMore && (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-[11.5px]"
          onClick={onLoadMore}
          disabled={isLoadingMore}
        >
          <T context="button to load older runs in the agent page history">
            Show Older Runs
          </T>
        </Button>
      )}
    </div>
  );
}

/**
 * Which days the History lists, by when each run started, in the viewer's
 * own time zone. Clearing it lists every run again.
 */
function RunDateRangeFilter({
  filter,
  onFilterChange,
}: {
  filter: RunHistoryFilter;
  onFilterChange: (next: RunHistoryFilter) => void;
}) {
  const gt = useGT();
  return (
    <DateRangePicker
      value={toDayPickerRange(filter.range)}
      onChange={(range) =>
        onFilterChange(withRange(filter, fromDayPickerRange(range)))
      }
      clearable
      placeholder={gt('Any date', {
        context:
          'agent page history date filter placeholder: no date range picked, runs from every day are listed',
      })}
      className="h-7 w-auto min-w-[9rem] text-[11.5px]"
    />
  );
}

function EmptyHistory({ reason }: { reason: HistoryEmptyReason }) {
  return (
    <p className="px-3 py-8 text-center text-sm text-muted-foreground">
      {reason === 'nothing-selected' ? (
        <T context="agent page run history empty state when the run-type filter has every kind of run unticked">
          No run types selected. Pick some under Run Types.
        </T>
      ) : reason === 'only-skipped' ? (
        <T context="agent page run history empty state when every run so far was skipped and those are hidden">
          No runs have done any work yet.
        </T>
      ) : reason === 'only-skipped-in-range' ? (
        <T context="agent page run history empty state when every run in the picked date range was skipped and those are hidden">
          No runs in this date range did any work.
        </T>
      ) : reason === 'none-in-range' ? (
        <T context="agent page run history empty state when no run started in the picked date range">
          No runs started in this date range.
        </T>
      ) : reason === 'filtered' ? (
        <T context="agent page run history empty state when the run-type filter hides every run">
          No runs match the selected run types.
        </T>
      ) : (
        <T context="agent page run history empty state">
          This agent has not run yet. Use Run to start it.
        </T>
      )}
    </p>
  );
}

/**
 * What the agent has done: its runs, newest first. Picking one opens it in the
 * detail card beside the list. By default skipped checks (runs the start gate
 * refused while the agent was only waiting) are left out; the "Run Types"
 * dropdown narrows by any kind of run, and the switch and the footer bring
 * skipped checks back.
 */
export function AgentRunHistory({
  runs,
  total,
  hiddenCount,
  categoryCounts,
  filter,
  onFilterChange,
  selectedRunId,
  onSelectRun,
  onLoadMore,
  isLoadingMore,
}: {
  runs: AgentRunSummary[];
  /** Runs matching the filter, loaded or not. */
  total: number;
  /** Runs the filter left out; null when unknown. */
  hiddenCount: number | null;
  /** Runs per category before the filter; null when unknown. */
  categoryCounts: Partial<Record<AgentRunCategory, number>> | null;
  filter: RunHistoryFilter;
  onFilterChange: (next: RunHistoryFilter) => void;
  selectedRunId: string | null;
  onSelectRun: (runId: string) => void;
  onLoadMore: () => void;
  isLoadingMore: boolean;
}) {
  const hidden = hiddenCount ?? 0;
  const onlySkippedHidden = hidesOnlySkipped(filter);
  return (
    <section className="flex max-h-[min(20rem,40vh)] min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-white lg:max-h-[min(36rem,60vh)]">
      <header className="border-b border-border px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <h2 className="flex items-center gap-1.5 text-sm font-semibold">
            <ClockCounterClockwiseIcon className="size-4 text-stone-500" />
            <T context="agent page run history heading">History</T>
          </h2>
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {total}
          </span>
        </div>
        <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
          <span className="flex flex-wrap items-center gap-2">
            <RunCategoryFilter
              filter={filter}
              onFilterChange={onFilterChange}
              counts={categoryCounts}
            />
            <RunDateRangeFilter
              filter={filter}
              onFilterChange={onFilterChange}
            />
          </span>
          <HideSkippedSwitch filter={filter} onFilterChange={onFilterChange} />
        </div>
      </header>
      {runs.length === 0 ? (
        <EmptyHistory reason={historyEmptyReason(filter, hiddenCount)} />
      ) : (
        <ul className="min-h-0 flex-1 divide-y divide-border overflow-y-auto">
          {runs.map((run) => (
            <RunRow
              key={run.id}
              run={run}
              selected={run.id === selectedRunId}
              onSelect={() => onSelectRun(run.id)}
            />
          ))}
        </ul>
      )}
      <HistoryFooter
        hiddenCount={hidden}
        onlySkippedHidden={onlySkippedHidden}
        onShowHidden={() =>
          onFilterChange(
            onlySkippedHidden
              ? withSkippedHidden(filter, false)
              : excludingCategories([], filter.range),
          )
        }
        canLoadMore={runs.length < total}
        onLoadMore={onLoadMore}
        isLoadingMore={isLoadingMore}
      />
    </section>
  );
}
